/* eslint-disable i18next/no-literal-string */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";
import { imageUrl } from "~/lib/image-url";

/**
 * Ported from bouncieweb's `components/LandingPageComponents/Compatibility.js`
 * (the OBD2 year/make/model compatibility checker) plus the three sibling
 * components it composed: `VehicleCompatibilityChecker`,
 * `VehicleCompatibilityResults`, and `VehicleCompatibilityInstallationInstructions`.
 *
 * REDUX REPLACEMENT
 * -----------------
 * The original derived make/model/year/results from a Redux
 * `vehicleCompatibility` slice (redux-actions thunks in
 * `services/vehicleCompatibility/*`). This port replaces that entire slice with
 * local `useState` plus async fetch helpers that hit the same public Bouncie OBD
 * endpoints the thunks used (`/obd/obdinstall/...`, base path `/api`). Selection
 * cascade, loading flags, results shape, and the trims -> installation-instructions
 * href chaining are reproduced faithfully. All network calls happen inside event
 * handlers (SSR-safe — see rule 10).
 *
 * The OBD graphic gifs were `public/images/obd-*.gif` in the old app; they now
 * live in this project's `media/images/products/compatibility/obd-locators/` (shared with the PDP compatibility drawer) and are referenced by URL.
 *
 * Breakpoint map: xsm 431 -> min-[431px]:  sm 640 -> sm:  md 768 -> md:  lg 1024 -> lg:
 */

// ---------------------------------------------------------------------------
// API layer (replaces the Redux thunks)
// ---------------------------------------------------------------------------

// Original request module defaulted apiPath to '/api'; endpoints resolve against
// the production Bouncie API (this landing page has no local backend/proxy).
const API_BASE = "https://www.bouncie.com/api";

interface Option {
  id: string;
  display: string;
}

interface ResultsData {
  compatibility: string;
  compatible: boolean;
  description: string | null;
  graphic: number | string | null;
  image: string | null;
}

async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Accept: "application/json" },
  });
  if (!(res.status >= 200 && res.status < 300)) {
    const error = new Error(res.statusText) as Error & { status?: number };
    error.status = res.status;
    throw error;
  }
  return res.json() as Promise<T>;
}

// The live API normally returns a bare array of { id, display }, but a
// cross-origin/error response can be an object (e.g. a `{ data: [...] }` wrapper
// or an error payload). Coerce any shape to a clean Option[] so a malformed
// response degrades to empty dropdowns rather than crashing the render.
function asOptions(payload: unknown): Option[] {
  const raw = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { data?: unknown })?.data)
      ? ((payload as { data: unknown[] }).data)
      : [];
  return raw
    .filter(
      (item): item is { id: unknown; display: unknown } =>
        typeof item === "object" && item !== null && "display" in item,
    )
    .map((item) => ({ id: String(item.id ?? ""), display: String(item.display ?? "") }));
}

// Reproduces `deriveEndpointFromHref`: take data[0].href, split on '/', drop the
// first 3 segments (protocol, '', host), and re-root the path.
function deriveEndpointFromHref(payload: {
  data?: { href?: string }[];
}): string {
  const href = payload?.data?.[0]?.href ?? "";
  return (
    "/" +
    href
      .split("/")
      .slice(3)
      .join("/")
  );
}

const NO_GRAPHIC_DESCRIPTION =
  "Unfortunately, we don't have a picture showing the exact location of your OBD Port. Don't worry; it should be easy to locate in the area under the steering column.";

// ---------------------------------------------------------------------------
// Autocomplete dropdown (replaces AutocompleteDropdown + ReactAutocomplete)
// ---------------------------------------------------------------------------

interface DropdownProps {
  title: string;
  items: Option[];
  selected: string;
  loading: boolean;
  onSelect: (display: string, id: string) => void;
  inputType?: string;
}

function Dropdown({
  title,
  items,
  selected,
  loading,
  onSelect,
  inputType = "text",
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the filter input when the dropdown opens (replaces the original
  // `autoFocus` prop, which the a11y lint rule disallows).
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    window.addEventListener("click", handleOutsideClick);
    return () => window.removeEventListener("click", handleOutsideClick);
  }, []);

  // Guard against a non-array response from the live API so a malformed payload
  // degrades to an empty dropdown instead of crashing the whole page.
  const safeItems = Array.isArray(items) ? items : [];
  const disabled = loading || safeItems.length === 0;

  const toggle = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (item: Option) => {
    setIsOpen(false);
    setFilter("");
    onSelect(item.display, item.id);
  };

  const label = loading ? "Loading..." : selected || title;

  const visibleItems = safeItems.filter((item) =>
    item.display.toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative mt-2.5 w-full",
        "min-[431px]:mr-2 min-[431px]:mt-2.5",
        "md:mx-2 md:max-w-[240px]",
      )}
    >
      <button data-sid={`obd-compatibility-toggle:${title}`}
        type="button"
        className={cn(
          "trigger-gtm-autocomplete-toggle flex h-[55px] w-full items-center rounded-[4px] border border-[#A8A8A8] bg-[#F8F8F8]",
          disabled && "opacity-50",
        )}
        onClick={toggle}
      >
        <div
          className="flex-1 text-center text-[16px] capitalize"
          style={{ color: colors.black }}
        >
          {label.toLowerCase()}
        </div>
        {/* Arrow */}
        <svg
          className="mr-4"
          width="10"
          height="10"
          viewBox="0 0 10 10"
          style={{ transform: "rotate(45deg)" }}
          aria-hidden="true"
        >
          <path
            d="M2 2 L8 2 L8 8"
            fill="none"
            stroke={colors.black}
            strokeWidth="2"
          />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute left-0 z-[1] inline-block w-full">
          <input data-sid={`obd-compatibility-filter:${title}`}
            ref={inputRef}
            className="box-border h-[55px] w-full border-none pl-2.5 text-[16px] shadow-[inset_0_2px_12px_rgba(0,0,0,0.1)] outline-none"
            placeholder={title}
            type={inputType}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <div className="max-h-[300px] overflow-scroll">
            {visibleItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className="flex h-[55px] w-full items-center pl-2.5 text-left text-[16px]"
                style={{ background: colors.grayLight2, color: colors.black }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(item)}
              >
                {item.display}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Round loading spinner (replaces RoundLoadingAnimation)
// ---------------------------------------------------------------------------

function RoundLoadingAnimation() {
  return (
    <div
      className="h-10 w-10 animate-spin rounded-full"
      style={{
        border: `5px solid ${colors.grayLight2}`,
        borderTop: `5px solid ${colors.purple}`,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Installation instructions (replaces VehicleCompatibilityInstallationInstructions)
// ---------------------------------------------------------------------------

// getGraphicUrl: graphics 2..4 share one gif; others map 1:1.
function getGraphicUrl(graphicNumber: number): string {
  if (graphicNumber > 1 && graphicNumber < 5) {
    return imageUrl("/images/products/compatibility/obd-locators/obd-2-4.gif");
  }
  return imageUrl(`/images/products/compatibility/obd-locators/obd-${graphicNumber}.gif`);
}

function InstallationInstructions({
  description,
  graphic,
  image,
  selectedYear,
  selectedMake,
  selectedModel,
  loading,
}: {
  description: string | null;
  graphic: number | string | null;
  image: string | null;
  selectedYear: string;
  selectedMake: string;
  selectedModel: string;
  loading: boolean;
}) {
  if (!description || !selectedYear || loading) return null;

  const graphicNumber = graphic ? Number(graphic) : 0;

  return (
    <div id="obdInformation" className="mx-auto max-w-[1200px]">
      {/* obdInformationTextBody */}
      <div
        className={cn(
          "font-display mx-auto max-w-[980px] pt-4 text-center text-[16px] leading-[21px]",
          "lg:pt-0 lg:text-[18px] lg:leading-[27px]",
        )}
        style={{ color: colors.zachBlack }}
      >
        {graphicNumber
          ? `Your OBD port can be found ${description.toLowerCase()}.`
          : description}
      </div>
      {graphicNumber ? (
        // obdImagesContainer
        <div className="flex w-full flex-col items-center pt-[15px]">
          <div className="my-5 max-w-[560px] overflow-hidden rounded-[10px] shadow-[0px_14px_24px_rgba(0,0,0,0.25)]">
            <img
              alt="obd-graphic"
              // eslint-disable-next-line local/no-raw-img -- animated GIF: Picture's AVIF/WebP/srcset don't apply
              src={getGraphicUrl(graphicNumber)}
              className="block w-full"
            />
          </div>
          {image && (
            <div
              className="rounded-[10px] p-[21px] shadow-[0px_12px_24px_rgba(0,0,0,0.05),inset_0px_0px_5px_rgba(0,0,0,0.28)]"
              style={{ backgroundColor: colors.white }}
            >
              <div className="mb-[15px] max-w-[560px] overflow-hidden rounded-[5px]">
                {/* eslint-disable-next-line local/no-raw-img -- external vehicle image URL from the compatibility API */}
                <img alt="obd-location" src={image} className="block w-full" />
              </div>
              <div className="text-center text-[34px] leading-[60px] md:text-[44px] md:leading-[70px]">
                {selectedYear} {selectedMake} {selectedModel}
              </div>
              <div
                className="text-center text-[14px]"
                style={{ color: colors.zachBlack }}
              >
                OBD images are user provided and may vary slightly in your
                vehicle.
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compatibility (main component)
// ---------------------------------------------------------------------------

interface ObdCompatibilityProps {
  comboTop?: boolean;
  customMessage?: React.ReactNode;
}

export default function ObdCompatibility({
  comboTop,
  customMessage = false,
}: ObdCompatibilityProps) {
  // Make / model / year lists + selections (replaces the Redux slice).
  const [makes, setMakes] = useState<Option[]>([]);
  const [models, setModels] = useState<Option[]>([]);
  const [years, setYears] = useState<Option[]>([]);

  const [makesLoading, setMakesLoading] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [yearsLoading, setYearsLoading] = useState(false);

  const [selectedMake, setSelectedMake] = useState("");
  const [selectedMakeId, setSelectedMakeId] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedModelId, setSelectedModelId] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  const [results, setResults] = useState<ResultsData | null>(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const resultsRef = useRef<HTMLDivElement>(null);

  const hasFetchedResults = results !== null;
  const isCompatible = results?.compatible ?? false;

  // fetchMakes on mount (browser-only fetch).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMakesLoading(true);
      try {
        const data = await apiGet<unknown>("/obd/obdinstall/makes");
        if (!cancelled) setMakes(asOptions(data));
      } catch {
        if (!cancelled) setMakes([]);
      } finally {
        if (!cancelled) setMakesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleMakeSelect = useCallback(
    async (make: string, id: string) => {
      setSelectedMake(make);
      setSelectedMakeId(id);
      // Reset downstream selections (cascade from the original thunks).
      setSelectedModel("");
      setSelectedModelId("");
      setSelectedYear("");
      setYears([]);
      setResults(null);
      setShowInstructions(false);

      setModelsLoading(true);
      try {
        const data = await apiGet<unknown>(
          `/obd/obdinstall/makes/${id}?make=${make}`,
        );
        setModels(asOptions(data));
      } catch {
        setModels([]);
      } finally {
        setModelsLoading(false);
      }
    },
    [],
  );

  const handleModelSelect = useCallback(
    async (model: string, id: string) => {
      setSelectedModel(model);
      setSelectedModelId(id);
      setSelectedYear("");
      setResults(null);
      setShowInstructions(false);

      setYearsLoading(true);
      try {
        const data = await apiGet<unknown>(
          `/obd/obdinstall/makes/${selectedMakeId}/${id}/?make=${selectedMake}&model=${model}`,
        );
        setYears(asOptions(data));
      } catch {
        setYears([]);
      } finally {
        setYearsLoading(false);
      }
    },
    [selectedMakeId, selectedMake],
  );

  const handleYearSelect = useCallback((year: string) => {
    setResults(null);
    setShowInstructions(false);
    setSelectedYear(year);
  }, []);

  const handleVerifyObdCompatibility = useCallback(async () => {
    if (!selectedYear) return;
    setResultsLoading(true);
    setResults(null);
    setShowInstructions(false);
    try {
      const obdVehicleCompatibilityData = await apiGet<
        ResultsData & { data?: { href?: string }[] }
      >(
        `/obd/obdinstall/makes/${selectedMakeId}/${selectedModelId}/${selectedYear}/?make=${selectedMake}&model=${selectedModel}`,
      );

      const { compatibility, compatible } = obdVehicleCompatibilityData;

      // Incompatible short-circuit.
      if (compatibility === "incompatible" && compatible === false) {
        setResults({
          compatibility,
          compatible,
          description: null,
          graphic: null,
          image: null,
        });
        return;
      }

      // No trims href: show the generic "no picture" description.
      if (!("data" in obdVehicleCompatibilityData) || !obdVehicleCompatibilityData.data) {
        setResults({
          compatibility,
          compatible,
          description: NO_GRAPHIC_DESCRIPTION,
          graphic: null,
          image: null,
        });
        return;
      }

      // Chain: results -> trims -> installation instructions.
      const trimsEndpoint = deriveEndpointFromHref(obdVehicleCompatibilityData);
      const trimsData = await apiGet<{ data?: { href?: string }[] }>(
        trimsEndpoint,
      );
      const installEndpoint = deriveEndpointFromHref(trimsData);
      const installResponse = await apiGet<{
        data?: {
          description?: string;
          graphic?: number | string;
          image?: string;
        };
      }>(installEndpoint);
      const installData = installResponse.data;

      if (!installData || Object.keys(installData).length === 0) {
        setResults({
          compatibility,
          compatible,
          description: NO_GRAPHIC_DESCRIPTION,
          graphic: null,
          image: null,
        });
        return;
      }

      setResults({
        compatibility,
        compatible,
        description: installData.description ?? null,
        graphic: installData.graphic ?? null,
        image: installData.image ?? null,
      });
    } catch {
      setResults(null);
    } finally {
      setResultsLoading(false);
    }
  }, [
    selectedMakeId,
    selectedModelId,
    selectedYear,
    selectedMake,
    selectedModel,
  ]);

  // Scroll results into view when they arrive (mirrors the original setTimeout).
  useEffect(() => {
    if (hasFetchedResults && !resultsLoading) {
      const timer = setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [hasFetchedResults, resultsLoading]);

  // CompatibilitySection base + comboTop variant.
  const sectionStyle: CSSProperties = { background: colors.blueLight4 };

  return (
    <div
      className={cn(
        "relative mb-16 rounded-[20px] p-8",
        comboTop ? "rounded-t-none" : "mt-8",
      )}
      style={sectionStyle}
    >
      {/* CompatibilityLayout */}
      <div className="flex flex-col items-center justify-between md:flex-row lg:mx-12">
        {customMessage ? (
          <h2 className="font-display text-center text-[8.5vw] leading-[1.2] font-extrabold [text-wrap:wrap] min-[431px]:text-[7vw] sm:text-[28px] md:text-[40px]">
            {customMessage}
          </h2>
        ) : (
          // CompatibilityTextSection
          <div className="max-w-none text-center md:max-w-[24rem] md:pr-8 lg:max-w-[26rem]">
            {/* CompatibilityCheckerHeading */}
            <h2
              className={cn(
                "font-display font-extrabold leading-[1.2]",
                "text-[8.5vw]",
                "min-[431px]:px-[8vw] min-[431px]:text-[7vw]",
                "sm:px-0 sm:text-[28px]",
                "md:text-left md:text-[40px]",
              )}
            >
              Find your vehicle&rsquo;s OBD2 port
            </h2>
            {/* CompatibilityCheckerP */}
            <p className="mt-4 mb-0 text-[16px] md:text-left">
              Works in 1996 and newer passenger vehicles with an OBD2 port. Some
              EVs may not be compatible.
            </p>
          </div>
        )}

        {/* Vehicle compatibility checker (vertical display). */}
        <div className="relative z-[1] text-center">
          <div className="w-[300px] pt-[17px] pb-[17px] text-center">
            <Dropdown
              title="Make"
              items={makes}
              selected={selectedMake}
              loading={makesLoading}
              onSelect={handleMakeSelect}
            />
            <Dropdown
              title="Model"
              items={models}
              selected={selectedModel}
              loading={modelsLoading}
              onSelect={handleModelSelect}
            />
            <Dropdown
              title="Year"
              items={years}
              selected={selectedYear}
              loading={yearsLoading}
              onSelect={handleYearSelect}
              inputType="tel"
            />
          </div>
          {/* VerifyCompatibilityButton */}
          <button data-sid="obd-compatibility-confirm"
            type="button"
            className={cn(
              "trigger-gtm-confirm-compatibility h-[50px] w-full rounded-[10px] text-[16px]",
              selectedYear
                ? "cursor-pointer text-white"
                : "cursor-not-allowed border-2 bg-transparent",
            )}
            style={
              selectedYear
                ? { backgroundColor: colors.fadedBlack2, color: colors.white }
                : {
                    color: colors.fadedBlack2,
                    borderColor: colors.fadedBlack2,
                    backgroundColor: "transparent",
                  }
            }
            onClick={handleVerifyObdCompatibility}
            disabled={!selectedYear}
          >
            Confirm compatibility &rsaquo;
          </button>
        </div>
      </div>

      {/* VehicleCompatibilityResults (paddingTop 57). */}
      <div ref={resultsRef}>
        {resultsLoading && (
          <div className="flex flex-col items-center py-[15px]">
            <RoundLoadingAnimation />
            <div
              className="mt-2.5 text-[22px] leading-[35px] lg:text-[32px] lg:leading-[47px]"
              style={{ color: colors.zachBlack }}
            >
              Loading Results...
            </div>
          </div>
        )}
        {hasFetchedResults && !resultsLoading && results && (
          <div className="pt-[57px]">
            {isCompatible || results.compatibility !== "incompatible" ? (
              <div className="flex flex-col items-center py-[15px]">
                <div className="text-[24px] leading-[35px]">
                  {selectedYear} {selectedMake} {selectedModel}
                </div>
                <div className="pt-2.5 text-[74px] leading-[85px]">👍</div>
                <div
                  className="text-[44px] leading-[65px] font-bold"
                  style={{ color: colors.purple }}
                >
                  Yes!
                </div>
                <div
                  className="text-center text-[22px] leading-[35px] lg:text-[32px] lg:leading-[47px]"
                  style={{ color: colors.zachBlack }}
                >
                  Bouncie will work with your vehicle.
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center py-[15px]">
                <div
                  className="mt-2.5 text-center text-[22px] leading-[35px] lg:text-[32px] lg:leading-[47px]"
                  style={{ color: colors.zachBlack }}
                >
                  We need a few more details to determine compatiblity.
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* "Where's my OBD port" toggle. */}
      {hasFetchedResults && (
        <div className="text-center">
          {isCompatible && (
            <button data-sid="obd-compatibility-instructions-toggle"
              type="button"
              className="cursor-pointer underline"
              style={{ color: colors.blue }}
              onClick={() => setShowInstructions((prev) => !prev)}
            >
              Where&rsquo;s my OBD port &rsaquo;
            </button>
          )}
        </div>
      )}

      {showInstructions && isCompatible && results && (
        <InstallationInstructions
          description={results.description}
          graphic={results.graphic}
          image={results.image}
          selectedYear={selectedYear}
          selectedMake={selectedMake}
          selectedModel={selectedModel}
          loading={resultsLoading}
        />
      )}
    </div>
  );
}
